/**
 * WP6e / #46629 — stand-in compile for pasted workflow intake.
 *
 * The browser cannot reach the in-boundary local model. This scan is the
 * declared document-scan stand-in; admission, bind, and refusals stay in
 * @excellent/core/verification-workflow-intake.
 */

import type {
  IntakeCompileOutcome,
  IntakeFailClosedReason,
} from "@excellent/core/verification-workflow-intake";

export const SAMPLE_DOC = [
  "# Refund approval workflow",
  "",
  "When a support agent issues a refund over $5,000, a second reviewer must",
  "confirm the refund before it is submitted to the payment processor.",
  "The refund amount and currency are recorded on the ticket.",
  "Settlement is checked against the processor's own record the next day.",
].join("\n");

export const FAIL_CLOSED_CHOICES: { value: "" | IntakeFailClosedReason; label: string }[] = [
  { value: "", label: "Local model present" },
  { value: "local-model-absent", label: "Local model absent" },
  { value: "frontier-forbidden", label: "Frontier model offered" },
  { value: "local-model-unparseable", label: "Model output unparseable" },
];


export function standInCompile(doc: string): IntakeCompileOutcome {
  const trimmed = doc.trim();
  if (!trimmed) return { state: "fail-closed", reason: "empty-doc" };
  const quote = (re: RegExp): string | null => {
    const m = doc.match(re);
    return m ? m[0] : null;
  };
  const predicates: Extract<IntakeCompileOutcome, { state: "compiled" }>["predicates"] = [];
  const rejected: { draft: string; reason: string }[] = [];
  const confirmSpan = quote(/[^.\n]*\bconfirm\w*\b[^.\n]*/i);
  if (confirmSpan) {
    predicates.push({
      id: "p_confirm",
      kind: "checkpoint",
      tier: 1,
      expr: "exists(confirm_event) && confirm_event.actor != claim.author",
      falsifier: "an action of this kind with no confirm event by a different actor",
      source_span: confirmSpan.trim(),
    });
  }
  const amountSpan = quote(/[^.\n]*\b(amount|currency|\$[\d,]+)\b[^.\n]*/i);
  if (amountSpan) {
    predicates.push({
      id: "p_band",
      kind: "band",
      tier: 1,
      expr: "record.amount is present && record.currency is present",
      falsifier: "a record whose amount or currency is absent from the source system",
      source_span: amountSpan.trim(),
    });
  }
  const orderingSpan = quote(/[^.\n]*\bbefore\b[^.\n]*/i);
  if (orderingSpan) {
    predicates.push({
      id: "p_order",
      kind: "ordering",
      tier: 1,
      expr: "confirm_event.at < submit_event.at",
      falsifier: "a submission timestamped before its own confirmation",
      source_span: orderingSpan.trim(),
    });
  }
  const requiredPassSpan = quote(/[^.\n]*\b(?:must|should|required|pass(?:es|ed)?|verify|verified|check(?:ed|s)?)\b[^.\n]*/i);
  if (requiredPassSpan && !confirmSpan && !orderingSpan) {
    predicates.push({
      id: "p_required_pass",
      kind: "checkpoint",
      tier: 1,
      expr: "independent_oracle.result == pass",
      falsifier: "the named independent check returns a non-passing result",
      source_span: requiredPassSpan.trim(),
    });
  }
  const settlementSpan = quote(/[^.\n]*\bsettle\w*\b[^.\n]*/i);
  if (settlementSpan) {
    predicates.push({
      id: "p_settle",
      kind: "external-oracle",
      tier: 2,
      expr: "external.settlement_record matches record.amount",
      falsifier: "a settlement in the processor's record that disagrees with ours",
      source_span: settlementSpan.trim(),
    });
  } else {
    predicates.push({
      id: "p_settle_invented",
      kind: "external-oracle",
      tier: 2,
      expr: "external.settlement_record matches record.amount",
      falsifier: "a settlement in the processor's record that disagrees with ours",
      source_span: "settlement is reconciled against the processor nightly",
    });
  }
  if (predicates.length === 0) {
    rejected.push({
      draft: "(no mechanical predicate found)",
      reason: "the document names no checkpoint, threshold, ordering or external record",
    });
  }
  return {
    state: "compiled",
    predicates,
    rejected,
    model: "stand-in/document-scan",
    attempts: 1,
  };
}
